import { button, e } from "./dom";
import { icon } from "./icons";
import { avatar, teamMark } from "./views/shared";
import { standings } from "./views/match";
import type { AppState, Room } from "./types";
import type { Settings } from "./settings";

export class Dialogs {
  private root = document.querySelector<HTMLElement>("#dialog-root")!;
  private previousFocus?: HTMLElement;
  private modalFocus?: HTMLElement;
  private scoreboardKey = "";
  kind: string | null = null;
  onChange: () => void = () => {};
  constructor() {
    document.addEventListener("focusin", (event) => {
      if (!this.kind) return;
      if (this.root.contains(event.target as Node)) {
        this.modalFocus = event.target as HTMLElement;
        return;
      }
      // A newly initialized native canvas may request focus asynchronously.
      // Keep that request from interrupting a dialog field being edited.
      const target = this.modalFocus?.isConnected
        ? this.modalFocus
        : (this.root.querySelector<HTMLElement>("[autofocus]") ??
          this.root.querySelector<HTMLElement>("input, button"));
      target?.focus({ preventScroll: true });
    });
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Tab") return;
      const items = [
        ...this.root.querySelectorAll<HTMLElement>(
          "button:not(:disabled),input:not(:disabled),select:not(:disabled),a[href]",
        ),
      ];
      const first = items[0],
        last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    });
  }
  open(kind: string, title: string, content: string, wide = false): void {
    if (!this.kind) this.previousFocus = document.activeElement as HTMLElement;
    this.kind = kind;
    this.modalFocus = undefined;
    this.scoreboardKey = "";
    this.root.innerHTML = `<div class="dialog-backdrop"><section class="panel dialog ${wide ? "wide-dialog" : ""}" role="dialog" aria-modal="true" aria-labelledby="dialog-title"><header class="dialog-header"><div><span class="eyebrow">Warlock</span><h2 id="dialog-title">${e(title)}</h2></div><button class="icon-button" data-action="close-dialog" aria-label="Close">${icon("close")}</button></header>${content}</section></div>`;
    document.querySelector<HTMLElement>("#app")!.inert = true;
    this.onChange();
    const target =
      this.root.querySelector<HTMLElement>("[autofocus]") ??
      this.root.querySelector<HTMLElement>("input, button");
    target?.focus({ preventScroll: true });
  }
  refresh(state: AppState): void {
    if (this.kind !== "scoreboard" || !state.snapshot) return;
    const key = JSON.stringify([
      state.snapshot.players.map((p) => [
        p.name,
        p.round_wins,
        p.eliminations,
        p.gold,
      ]),
      state.snapshot.team_scores,
    ]);
    if (key === this.scoreboardKey) return;
    this.scoreboardKey = key;
    const table = this.root.querySelector(".standings");
    if (table) table.outerHTML = standings(state.snapshot, state.matchPlayerId);
  }
  close(): void {
    this.root.innerHTML = "";
    this.kind = null;
    document.querySelector<HTMLElement>("#app")!.inert = false;
    this.previousFocus?.focus();
    this.onChange();
  }
  error(message: string): void {
    const node = this.root.querySelector<HTMLElement>("[data-form-error]");
    if (node) node.textContent = message;
  }
  rename(state: AppState): void {
    this.open(
      "rename",
      "Make a name for yourself",
      `<form data-form="rename"><label for="wizard-name">Wizard name</label><input id="wizard-name" name="name" value="${e(state.name)}" maxlength="24" minlength="1" autocomplete="nickname" autofocus required><p class="field-hint">1–24 characters. Your spells and scores stay with you.</p><p class="form-error" data-form-error role="alert"></p><div class="dialog-actions">${button("close-dialog", "Cancel")}<button class="button primary" type="submit">Save name</button></div></form>`,
    );
  }
  join(): void {
    this.open(
      "join",
      "Join your friends",
      `<form data-form="join"><p>Paste a room invite to join the game or watch.</p><label for="room-link">Room invite</label><input id="room-link" name="invite" type="text" placeholder="Paste your invite link" autofocus required autocomplete="off" spellcheck="false"><p class="form-error" data-form-error role="alert"></p><div class="dialog-actions">${button("close-dialog", "Cancel")}<button type="submit" class="button primary">Find room</button></div></form>`,
    );
  }
  entry(room: Room): void {
    const open = !room.started && room.slots.some((s) => s.open && !s.member);
    this.open(
      "entry",
      room.kind === "friends" ? "A room for friends" : "You’re invited",
      `<p>${room.started ? "The match is already underway. Watch the action now." : open ? "Your arena is ready. Step in or enjoy the view." : "All player seats are taken. There’s always room to watch."}</p><div class="entry-details"><span>${room.kind === "friends" ? "Friends room" : "Public match"}</span><span>${room.mode === "teams" ? "Teams · Friendly fire off" : "Free-for-all"}</span><span>${room.slots.filter((s) => s.member).length} / 8 players</span></div><div class="entry-actions">${open ? button("enter-play", "Join game", { primary: true }) : ""}${button("enter-watch", `${icon("eye")}Watch`, { primary: !open })}</div><p class="form-error" data-form-error role="alert"></p>`,
    );
  }
  invite(link: string, canRevoke: boolean): void {
    this.open(
      "invite",
      "Good company. Bad intentions.",
      `<p>Share this invite to play together or watch the match.</p><label for="invite-url">Room invite</label><input id="invite-url" readonly value="${e(link)}" autofocus><div class="dialog-actions">${canRevoke ? button("revoke-invite", "Replace invite", { small: true }) : "<span></span>"}${button("copy-invite", `${icon("link")}Copy invite`, { primary: true })}</div><p class="field-hint" data-copy-status aria-live="polite">Anyone with this link can enter the room.</p>`,
    );
  }
  menu(state: AppState): void {
    this.open(
      "menu",
      "A moment by the fire",
      `<p class="menu-name">${avatar(state.playerId, true)}${e(state.name)}<button data-action="rename" class="text-button">Rename</button></p><nav class="menu-actions" aria-label="Match menu">${button("close-dialog", "Resume", { primary: true })}${state.room ? button("invite", `${icon("link")}Invite friends`) : ""}${button("settings", `${icon("settings")}Settings`)}${button("controls", "Controls")}${state.snapshot ? button("scoreboard", "Standings") : ""}${button("leave", "Leave match")}</nav><p class="field-hint">The match continues while this menu is open.</p>`,
    );
  }
  controls(): void {
    this.open(
      "controls",
      "A little magic goes a long way",
      `<div class="control-list"><div><span>Move</span><b><kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd></b></div><div><span>Aim independently</span><b>Mouse</b></div><div><span>Spell slot 1</span><kbd>Left click</kbd></div><div><span>Spell slots 2–4</span><b><kbd>Right click</kbd><kbd>Space</kbd><kbd>Q</kbd></b></div><div><span>Standings</span><kbd>Tab</kbd></div><div><span>Menu / cancel matchmaking</span><kbd>Esc</kbd></div><div><span>Switch watched wizard</span><b><kbd>[</kbd><kbd>]</kbd></b></div></div><p>Drag spells between any of the four slots in the between-round shop before selecting Ready.</p><p>Offensive spells hurt and push your rivals. Lava burns away HP, so hurry back onto solid ground. At zero HP, you’re eliminated until the next round. Pillars block Fireballs; the island keeps shrinking as you fight.</p>${button("close-dialog", "Into the arena", { primary: true })}`,
    );
  }
  settings(settings: Settings): void {
    const range = (
      name: keyof Settings,
      label: string,
      min: number,
      max: number,
      step: number,
      suffix = "",
    ) =>
      `<label class="setting-row"><span>${label}</span><input type="range" name="${name}" min="${min}" max="${max}" step="${step}" value="${settings[name]}" aria-label="${label}"><output data-output="${name}">${settings[name]}${suffix}</output></label>`;
    this.open(
      "settings",
      "Make yourself comfortable",
      `<form data-form="settings"><div class="settings-section"><h3>Sound</h3>${range("masterVolume", "Master volume", 0, 100, 1, "%")}${range("effectsVolume", "Spell effects", 0, 100, 1, "%")}</div><div class="settings-section"><h3>Picture</h3><label class="setting-row"><span>Visual quality</span><select name="quality">${["high", "medium", "low"].map((q) => `<option value="${q}" ${settings.quality === q ? "selected" : ""}>${q[0].toUpperCase() + q.slice(1)}</option>`).join("")}</select></label><label class="setting-row"><span>Reduce decorative motion</span><input name="reducedMotion" type="checkbox" ${settings.reducedMotion ? "checked" : ""}></label><label class="setting-row"><span>Show performance</span><input name="showPerformance" type="checkbox" ${settings.showPerformance ? "checked" : ""}></label></div><p class="field-hint">Camera orientation and scale stay fixed during a round.</p><div class="dialog-actions">${button("reset-settings", "Reset defaults")}<button type="submit" class="button primary">Done</button></div></form>`,
    );
  }
  scoreboard(state: AppState): void {
    if (!state.snapshot) return;
    this.open(
      "scoreboard",
      "The standings",
      `${standings(state.snapshot, state.matchPlayerId)}<div class="dialog-actions">${button("close-dialog", "Back", { primary: true })}</div>`,
      true,
    );
  }
  follow(state: AppState): void {
    this.open(
      "follow",
      "Choose your wizard",
      `<div class="follow-list">${state.snapshot?.players.map((p) => `<button data-action="follow" data-player="${p.id}" class="follow-option ${p.id === state.followedPlayerId ? "selected" : ""}">${avatar(p.id, true)}${teamMark(p.team)}<span>${e(p.name)}</span><small>${p.alive ? "In the arena" : "Eliminated"}</small></button>`).join("")}</div>`,
    );
  }
}
